import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SessionStorageService } from '../../services/session-storage.service';
import { WorkforcePerson } from '../../accounts/workforce-cash/workforce-cash.models';
import { DeliveryCashRequest, LiabilityOption, PettyCashBox, PettyCashCount, PettyCashCustodyTransfer,
  PettyCashDocument, PettyCashLine, PettyCashOption, PettyCashPartyOption, PettyCashService,
  PettyCashWorkerOptions } from '../../accounts/workforce-cash/petty-cash.service';

type CashAction = 'REQUEST_FUNDS' | 'RECEIVE_FUNDS' | 'PAY_WORKER' | 'PURCHASE' | 'RETURN' | 'REIMBURSE' | 'COUNT' | 'ACCEPT' | 'WAGE_RETURN';

@Component({
  selector: 'app-petty-cash-custodian', standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule],
  templateUrl: './petty-cash-custodian.component.html', styleUrls: ['./petty-cash-custodian.component.css']
})
export class PettyCashCustodianComponent implements OnInit {
  boxes: PettyCashBox[] = []; selectedBoxId?: number; book: PettyCashDocument[] = [];
  pending: PettyCashDocument[] = []; counts: PettyCashCount[] = []; transfers: PettyCashCustodyTransfer[] = [];
  people: WorkforcePerson[] = []; workerOptions?: PettyCashWorkerOptions; expenseAccounts: PettyCashOption[] = [];
  parties: PettyCashPartyOption[] = []; partySearch = ''; liabilities: LiabilityOption[] = [];
  selectedLiability?: LiabilityOption;
  action?: CashAction; selectedDocument?: PettyCashDocument; selectedTransfer?: PettyCashCustodyTransfer;
  line: PettyCashLine = { purpose: 'FLOAT_ISSUE', amount: 0 };
  amount = 0; reason = ''; witness = ''; proof = ''; countedCash?: number;
  dialogProofFile?: File; activeUploadDoc?: PettyCashDocument;
  selectedParty?: PettyCashPartyOption; loading = false; busy = false; error = ''; success = '';
  private idempotencyKey = '';
  readonly payPurposes = [
    { value: 'FLOAT_ISSUE', label: 'Company expense float' },
    { value: 'FLOAT_TOP_UP', label: 'Top up company expense float' },
    { value: 'PAY_ADVANCE', label: 'Advance against future pay' },
    { value: 'EARNED_PAY', label: 'Earned pay from posted obligation' }
  ];
  readonly returnPurposes = [
    { value: 'FLOAT_RETURN', label: 'Return unused company float' },
    { value: 'PAY_ADVANCE_REPAY', label: 'Repay advance against pay' }
  ];
  constructor(private petty: PettyCashService, private session: SessionStorageService) {}
  get companyId(): number | undefined { return this.session.getCompany()?.id; }
  get selectedBox(): PettyCashBox | undefined { return this.boxes.find(box => box.pettyCashBoxId === this.selectedBoxId); }
  get title(): string { return ({ REQUEST_FUNDS: 'Request funds', RECEIVE_FUNDS: 'Confirm cash received', PAY_WORKER: 'Pay worker',
    PURCHASE: 'Record purchase', RETURN: 'Receive returned cash', REIMBURSE: 'Reimburse worker', COUNT: 'Count cash',
    ACCEPT: 'Accept cash box custody', WAGE_RETURN: 'Record returned wage cash' } as Record<CashAction, string>)[this.action || 'REQUEST_FUNDS']; }
  get active(): boolean { return this.selectedBox?.status === 'ACTIVE'; }
  get postedBook(): PettyCashDocument[] { return this.book.filter(doc => doc.status === 'POSTED'); }
  get pendingPayments(): number { return this.pending.filter(doc => !['FUND', 'FLOAT_RETURN', 'PAY_ADVANCE_REPAY'].includes(doc.purpose)).length; }
  get funding(): PettyCashDocument[] { return this.pending.filter(doc => doc.purpose === 'FUND' && doc.sourceKind === 'CASH_BOX_TOP_UP_REQUEST'); }
  get pendingHandoffs(): PettyCashDocument[] { return this.pending.filter(doc => doc.sourceKind !== 'CASH_BOX_TOP_UP_REQUEST'); }
  get approvedSettlements() { return this.workerOptions?.settlements.filter(row => row.status === 'APPROVED' && row.outOfPocket) || []; }
  get topUpSettlements() { return this.workerOptions?.settlements.filter(row => row.status === 'TOP_UP_DUE') || []; }
  get openAdvances() { return this.workerOptions?.payAdvances.filter(row => Number(row.openAmount) > 0) || []; }
  get releasedPayouts() { return this.workerOptions?.payouts.filter(row => row.status === 'RELEASED') || []; }
  get availableFloatRequests() { return this.workerOptions?.advances.filter(row => row.status === 'APPROVED') || []; }
  get deliveryRequests(): DeliveryCashRequest[] { return (this.workerOptions?.deliveryCashRequests || [])
    .filter(row => row.status === 'DRAFT' && row.purpose === 'ADVANCE_AGAINST_PAY'
      && (!row.pettycashboxid || row.pettycashboxid === this.selectedBoxId)); }
  ngOnInit(): void { this.load(); }
  load(): void {
    if (!this.companyId) { this.error = 'Select a company to open petty cash.'; return; }
    this.loading = true; this.error = '';
    forkJoin({ boxes: this.petty.myBoxes(this.companyId), transfers: this.petty.myTransfers(this.companyId) }).subscribe({
      next: ({ boxes, transfers }) => {
        this.boxes = boxes; this.transfers = transfers;
        if (!boxes.some(box => box.pettyCashBoxId === this.selectedBoxId)) this.selectedBoxId = boxes[0]?.pettyCashBoxId;
        this.selectBox(); this.loading = false;
      }, error: err => { this.error = this.message(err, 'Petty cash could not be loaded.'); this.loading = false; }
    });
  }
  selectBox(): void {
    this.book = []; this.pending = []; this.counts = []; this.people = []; this.expenseAccounts = [];
    this.workerOptions = undefined;
    if (!this.companyId || !this.selectedBoxId) return;
    const boxId = this.selectedBoxId;
    forkJoin({ book: this.petty.myBook(this.companyId, boxId),
      pending: this.petty.myPending(this.companyId, boxId),
      counts: this.petty.myCounts(this.companyId, boxId),
      people: this.petty.myPeople(this.companyId, boxId),
      accounts: this.petty.myExpenseAccounts(this.companyId, boxId) }).subscribe({
      next: rows => { if (this.selectedBoxId !== boxId) return;
        this.book = rows.book; this.pending = rows.pending; this.counts = rows.counts;
        this.people = rows.people; this.expenseAccounts = rows.accounts; },
      error: err => { if (this.selectedBoxId === boxId) this.error = this.message(err, 'Cash book or options could not be loaded.'); }
    });
  }
  open(action: CashAction, document?: PettyCashDocument, transfer?: PettyCashCustodyTransfer): void {
    this.action = action; this.selectedDocument = document; this.selectedTransfer = transfer;
    this.line = { purpose: action === 'RETURN' ? 'FLOAT_RETURN' : action === 'PURCHASE' ? 'DIRECT_EXPENSE'
      : action === 'REIMBURSE' ? 'REIMBURSE_EXPENSE' : 'FLOAT_ISSUE', amount: 0 };
    this.amount = document ? Number(document.amount) : 0;
    this.countedCash = action === 'ACCEPT' ? undefined : Number(this.selectedBox?.currentBalance || 0);
    this.reason = ''; this.witness = ''; this.proof = ''; this.dialogProofFile = undefined;
    this.activeUploadDoc = undefined; this.selectedParty = undefined;
    this.workerOptions = undefined; this.liabilities = []; this.selectedLiability = undefined;
    this.error = ''; this.idempotencyKey = crypto.randomUUID();
  }
  workerChanged(): void {
    this.workerOptions = undefined; this.liabilities = [];
    if (!this.companyId || !this.selectedBoxId || !this.line.fieldWorkerId) return;
    this.petty.myWorkerOptions(this.companyId, this.selectedBoxId, this.line.fieldWorkerId).subscribe({
      next: options => this.workerOptions = options,
      error: err => this.error = this.message(err, 'Worker payment sources could not be loaded.')
    });
  }
  payoutChanged(): void {
    this.liabilities = []; this.selectedLiability = undefined; this.line.liabilityId = undefined;
    if (!this.companyId || !this.selectedBoxId || !this.line.payoutStatementId) return;
    this.petty.myLiabilities(this.companyId, this.selectedBoxId, this.line.payoutStatementId).subscribe({
      next: rows => this.liabilities = rows,
      error: err => this.error = this.message(err, 'Posted payout obligation could not be loaded.')
    });
  }
  selectLiability(option?: LiabilityOption): void {
    this.selectedLiability = option;
    this.line.liabilityId = option?.liabilityId; this.line.liabilityType = option?.liabilityType;
    this.line.liabilityAccountId = option?.accountId;
    if (option) this.line.amount = Math.min(Number(this.line.amount || option.remaining), Number(option.remaining));
  }
  searchParties(): void {
    if (!this.companyId || !this.selectedBoxId) return;
    this.petty.myParties(this.companyId, this.selectedBoxId, this.partySearch).subscribe({
      next: parties => this.parties = parties,
      error: err => this.error = this.message(err, 'Party search failed.')
    });
  }
  chooseParty(party?: PettyCashPartyOption): void {
    this.selectedParty = party; this.line.payeePartyId = party?.id; this.line.payeePartyType = party?.type;
    if (party) this.line.payeeName = party.name;
  }
  chooseSource(): void {
    const purpose = this.line.purpose;
    const source = Number(this.line.sourceId);
    if (purpose === 'FLOAT_ISSUE') {
      const advance = this.availableFloatRequests.find(row => row.workforceCashAdvanceId === source);
      this.line.sourceKind = advance ? 'WORKFORCE_CASH_ADVANCE' : undefined;
      if (advance) this.line.amount = Number(advance.approvedAmount || advance.requestedAmount);
    } else if (purpose === 'FLOAT_TOP_UP') {
      const settlement = this.topUpSettlements.find(row => row.workforceCashSettlementId === source);
      this.line.sourceKind = settlement ? 'WORKFORCE_SETTLEMENT_TOP_UP' : undefined;
      if (settlement) this.line.amount = Number(settlement.topUpAmount || 0);
    } else if (purpose === 'PAY_ADVANCE') {
      const request = this.deliveryRequests.find(row => row.deliverycashrequestid === source);
      this.line.sourceKind = request ? 'DELIVERY_CASH_REQUEST' : undefined;
      if (request) this.line.amount = Number(request.amount);
    } else if (purpose === 'REIMBURSE_EXPENSE') {
      const settlement = this.approvedSettlements.find(row => row.workforceCashSettlementId === source);
      this.line.sourceKind = settlement ? 'WORKFORCE_SETTLEMENT' : undefined;
      if (settlement) { this.line.amount = Number(settlement.approvedAmount || 0);
        this.line.expenseAccountId = settlement.lines?.[0]?.expenseAccountId; }
    }
  }
  purposeChanged(): void { const worker = this.line.fieldWorkerId; const purpose = this.line.purpose;
    this.line = { purpose, amount: 0, fieldWorkerId: worker }; this.liabilities = []; this.selectedLiability = undefined; }
  submit(printAfter = false): void {
    if (!this.companyId || !this.selectedBoxId || !this.action || this.busy) return;
    let request: Observable<unknown>;
    const company = this.companyId, box = this.selectedBoxId;
    if (this.action === 'REQUEST_FUNDS') {
      if (!(this.amount > 0)) { this.error = 'Enter a positive funding amount.'; return; }
      request = this.petty.requestFunding(company, box, this.amount, this.reason.trim(), this.idempotencyKey);
    } else if (this.action === 'RECEIVE_FUNDS') {
      if (!this.selectedDocument) return;
      if (!(this.proof.trim() || this.witness.trim() || this.dialogProofFile)) { this.error = 'Add a proof file, link, or named witness.'; return; }
      request = this.petty.receiveFunding(company, this.selectedDocument.pettyCashDocumentId,
        this.amount, this.proof.trim(), this.witness.trim());
    } else if (this.action === 'COUNT') {
      if (this.countedCash == null || this.countedCash < 0) { this.error = 'Enter the physical cash counted.'; return; }
      request = this.petty.myCount(company, box, this.countedCash, this.witness.trim());
    } else if (this.action === 'ACCEPT') {
      if (!this.selectedTransfer) return;
      if (this.countedCash == null || this.countedCash < 0) { this.error = 'Enter the physical cash counted.'; return; }
      request = this.petty.acceptTransfer(company, this.selectedTransfer.pettyCashCustodyTransferId, this.countedCash);
    } else if (this.action === 'WAGE_RETURN') {
      if (!this.selectedDocument || !this.reason.trim() || !(this.proof.trim() || this.witness.trim() || this.dialogProofFile)) {
        this.error = 'Enter a correction reason and signed proof or named witness.'; return;
      }
      request = this.petty.myEarnedPayReturn(company, this.selectedDocument.pettyCashDocumentId,
        this.reason.trim(), this.proof.trim(), this.witness.trim());
    } else {
      const line = { ...this.line, amount: Number(this.line.amount), proofUrl: this.proof.trim() || undefined,
        witnessName: this.witness.trim() || undefined, notes: this.reason.trim() || undefined };
      if (!(line.amount > 0)) { this.error = 'Enter a positive cash amount.'; return; }
      if (this.action !== 'PURCHASE' && !line.fieldWorkerId) { this.error = 'Choose a worker.'; return; }
      if (this.action === 'PURCHASE' && (!line.payeeName?.trim() || !line.expenseAccountId
          || !(line.receiptUrl?.trim() || line.missingReceiptReason?.trim()))) {
        this.error = 'Enter the recipient, expense account, and receipt link or missing-receipt reason.'; return;
      }
      if (this.action === 'REIMBURSE' && !line.sourceId) { this.error = 'Choose an approved out-of-pocket settlement.'; return; }
      if (line.purpose === 'EARNED_PAY' && (!line.payoutStatementId || !line.liabilityId)) {
        this.error = 'Choose a released payout and posted obligation.'; return;
      }
      if (line.purpose === 'PAY_ADVANCE_REPAY' && !line.workerPayAdvanceId) {
        this.error = 'Choose the advance being repaid.'; return;
      }
      request = this.petty.myHandoff(company, box, [line], this.idempotencyKey);
    }
    this.busy = true; this.error = '';
    const pendingProofFile = this.dialogProofFile;
    request.subscribe({ next: result => {
        if (this.action === 'RECEIVE_FUNDS' && (result as PettyCashDocument).fundingReportedAmount != null) {
          this.action = undefined; this.load();
          this.error = 'The amount received differs from the approved amount. Finance must resolve this funding request.';
          return;
        }
        const createdDocs = Array.isArray(result) ? (result as PettyCashDocument[]) : [result as PettyCashDocument];
        const created = createdDocs[0];
        if (pendingProofFile && created?.pettyCashDocumentId) {
          this.uploadDocProof(created, pendingProofFile);
        }
        if (printAfter && created) {
          this.downloadVoucher(created);
        }
        this.action = undefined; this.success = 'Cash record saved. The cash book changes when posting is complete.';
        this.load(); }, error: err => { this.error = this.message(err, 'Cash action failed.'); this.busy = false; },
      complete: () => this.busy = false });
  }

  onDialogFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files[0]) {
      this.dialogProofFile = input.files[0];
    }
  }

  clearDialogFile(input?: HTMLInputElement): void {
    this.dialogProofFile = undefined;
    if (input) input.value = '';
  }

  triggerDocUpload(doc: PettyCashDocument, input: HTMLInputElement): void {
    this.activeUploadDoc = doc;
    input.value = '';
    input.click();
  }

  onDocFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files[0] && this.activeUploadDoc) {
      this.uploadDocProof(this.activeUploadDoc, input.files[0]);
    }
  }

  uploadDocProof(doc: PettyCashDocument, file: File): void {
    if (!this.companyId || !doc.pettyCashDocumentId || !file) return;
    this.busy = true;
    this.petty.uploadWorkspaceProof(this.companyId, doc.pettyCashDocumentId, file).subscribe({
      next: updated => {
        this.busy = false;
        this.success = `Signed proof uploaded for ${doc.documentNumber}.`;
        this.load();
      },
      error: err => {
        this.busy = false;
        this.error = this.message(err, 'Failed to upload signed proof.');
      }
    });
  }

  downloadVoucher(doc: PettyCashDocument): void {
    if (!this.companyId || !doc.pettyCashDocumentId) return;
    this.petty.downloadWorkspaceVoucherPdf(this.companyId, doc.pettyCashDocumentId).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = `petty-cash-voucher-${doc.documentNumber || doc.pettyCashDocumentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      },
      error: err => {
        this.error = this.message(err, 'Could not generate voucher PDF from Reports API.');
      }
    });
  }
  amountText(amount?: number): string { return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  workerName(id?: number): string { return this.people.find(person => person.fieldWorkerId === id)?.displayName || (id ? `Worker #${id}` : ''); }
  isIn(doc: PettyCashDocument): boolean { return ['FUND', 'FLOAT_RETURN', 'PAY_ADVANCE_REPAY', 'EARNED_PAY_RETURN'].includes(doc.purpose); }
  private message(err: any, fallback: string): string { return err?.error?.message || err?.error?.detail || fallback; }
}
